import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Play } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useReducedMotion from '../../hooks/useReducedMotion';
import { bounceIn, maybe } from '../../lib/motionVariants';
import EmptyState from '../ui/EmptyState';

/**
 * ContinueLearningCard — hero card showing primary enrolled course with progress.
 *
 * Props:
 *   enrollment — enrollment object or null
 */
export default function ContinueLearningCard({ enrollment }) {
  const { t, getField } = useLanguage();
  const reduced = useReducedMotion();

  if (!enrollment || !enrollment.course) {
    return (
      <EmptyState
        icon={BookOpen}
        iconSize={56}
        title={t('dashboard.duo.noEnrollment.title')}
        sub={t('dashboard.duo.empty.sub')}
        ctaLabel={t('dashboard.duo.noEnrollment.cta')}
        ctaTo="/courses"
      />
    );
  }

  const course = enrollment.course;
  const courseId = course.course_id || course._id || course.id;
  const courseTitle = getField(course, 'title');

  // Progress calculation — defansif tum backend field varyasyonlari destekleniyor
  const totalLessons =
    enrollment.lessons?.length
    || enrollment.total_lessons
    || enrollment.lesson_count
    || enrollment.course?.lessons_count
    || course.lesson_count
    || course.lessons?.length
    || 0;

  const completedCount =
    enrollment.completed_lesson_ids?.length
    || enrollment.completed_lessons?.length
    || enrollment.completed_lessons_count
    || enrollment.lessons_completed
    || 0;

  const progressPct =
    enrollment.progress != null && Number.isFinite(enrollment.progress)
      ? Math.round(enrollment.progress)
      : (totalLessons > 0
          ? Math.round((completedCount / totalLessons) * 100)
          : 0);

  // Alias: progressLabel placeholder'lari icin
  const completedLessons = completedCount;
  const pct = progressPct;

  // Next lesson
  const nextLesson = enrollment.next_lesson || null;
  const nextLessonTitle = nextLesson
    ? getField(nextLesson, 'title')
    : null;

  // Continue destination — route format is /lesson/:courseId/:lessonId
  const nextLessonId = nextLesson
    ? (nextLesson.lesson_id || nextLesson._id || nextLesson.id)
    : null;
  const continueUrl = nextLessonId
    ? `/lesson/${courseId}/${nextLessonId}`
    : `/courses/${courseId}`;

  const progressLabel = t('dashboard.duo.lessonProgress')
    .replace('{{completed}}', completedLessons)
    .replace('{{total}}', totalLessons)
    .replace('{{pct}}', pct);

  return (
    <motion.article
      variants={maybe(reduced, bounceIn)}
      initial="hidden"
      animate="visible"
      className="card-duo p-5 mb-5"
      aria-label={courseTitle}
      data-testid="continue-learning-card"
    >
      {/* Desktop: 3-column grid; Mobile: flex column */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-4">

        {/* Left: course icon box — gold accent per Panel design */}
        <div
          className="w-[60px] h-[60px] rounded-2xl bg-[#C9A227] flex items-center justify-center shrink-0"
          aria-hidden="true"
        >
          <BookOpen size={28} strokeWidth={1.8} className="text-white" />
        </div>

        {/* Middle: course info + progress */}
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[#8a6d1a] mb-0.5">
            {t('dashboard.duo.myCourse')}
          </p>
          <h2
            className="font-duo-display text-lg text-[#0E1F3D] leading-tight truncate mb-2"
            data-testid="continue-course-title"
          >
            {courseTitle}
          </h2>
          {nextLessonTitle && (
            <p className="text-xs text-[var(--text-secondary)] mb-2 truncate">
              <span className="font-semibold">{t('dashboard.duo.lessonLabel')}:</span>{' '}
              {nextLessonTitle}
            </p>
          )}
          {/* Progress bar */}
          <div className="flex items-center gap-2.5">
            <div
              className="flex-1 h-2 rounded-full bg-[#eef2ef] overflow-hidden"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={progressLabel}
            >
              <div
                className="h-full rounded-full bg-[#34CC67] transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[13px] font-bold text-[#16794a] shrink-0">
              {pct}%
            </span>
          </div>
          <p className="text-xs text-[#6b7a70] mt-1.5">{progressLabel}</p>
        </div>

        {/* Right: CTA button */}
        <div className="flex justify-start md:justify-end">
          <Link
            to={continueUrl}
            className="inline-flex items-center gap-2 shrink-0 bg-[#16794a] hover:bg-[#0f5c38] text-white font-bold text-[15px] rounded-xl px-6 py-3.5 transition-colors"
            data-testid="continue-btn"
          >
            <Play size={16} strokeWidth={2.5} aria-hidden="true" />
            {t('dashboard.duo.continueBtn')}
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
